import React, { useEffect, useState } from 'react';
import styles from './InvoicingPage.module.css';
import InvoiceModal from '../../../components/resellers/inv/InvoiceModal';

const InvoicingPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchInvoices = () => {
    setLoading(true);
    fetch('https://kindred-cloudflare.shepherdn.workers.dev/api/invoices')
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Server responded with status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setInvoices(data);
        setPendingCount(data.filter(inv => inv.status === 'Pending').length);
        setOverdueCount(data.filter(inv => inv.status === 'Overdue').length);
        setError(null);
      })
      .catch((err) => {
        console.error('Error fetching invoices:', err);
        setError(`Failed to load invoices: ${err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const generateInvoice = (invoiceData) => {
    fetch('https://kindred-cloudflare.shepherdn.workers.dev/api/invoices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoiceData),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        fetchInvoices();
        setIsModalOpen(false);
      })
      .catch(err => {
        console.error('Error creating invoice:', err);
        setError(`Failed to create invoice: ${err.message}`);
      });
  };

  const resendInvoice = (inv) => {
    fetch('https://kindred-cloudflare.shepherdn.workers.dev/api/invoices', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        ...inv, 
        lastSent: new Date().toISOString() 
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        fetchInvoices();
      })
      .catch(err => {
        console.error('Error updating invoice:', err);
        setError(`Failed to update invoice: ${err.message}`);
      });
  };

  const deleteInvoice = (id) => {
    fetch('https://kindred-cloudflare.shepherdn.workers.dev/api/invoices', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Server responded with status: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        fetchInvoices();
      })
      .catch(err => {
        console.error('Error deleting invoice:', err);
        setError(`Failed to delete invoice: ${err.message}`);
      });
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.invoiceContainer}>
        {/* Error Display */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}
        
        {/* Invoice Summary Section */}
        <div className={styles.summarySection}>
          <div>
            <h2 className={styles.sectionTitle}>Monthly Invoice</h2>
            <div className={styles.chartPlaceholder}>
              <span className={styles.placeholderText}>
                {loading ? 'Loading data...' : 'Chart Placeholder'}
              </span>
            </div>
          </div>
          <div className={styles.statusSection}>
            <div className={styles.statusItem}>
              <div className={styles.pendingCircle}>{pendingCount}</div>
              <span className={styles.statusLabel}>Pending</span>
            </div>
            <div className={styles.statusItem}>
              <div className={styles.overdueCircle}>{overdueCount}</div>
              <span className={styles.statusLabel}>Overdue</span>
            </div>
          </div>
        </div>

        <button 
          onClick={() => setIsModalOpen(true)} 
          className={styles.addInvoiceButton}
          disabled={loading}
        >
          ➕ New Invoice
        </button>

        {/* Invoice Modal */}
        <InvoiceModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          onSave={generateInvoice}
        />

        {/* Invoice Details List */}
        {loading ? (
          <div className={styles.loadingIndicator}>Loading invoices...</div>
        ) : (
          <div className={styles.invoiceList}>
            {invoices.length === 0 ? (
              <div className={styles.emptyState}>
                No invoices found. Create a new invoice to get started.
              </div>
            ) : (
              invoices.map((inv) => (
                <div key={inv.id} className={styles.invoiceItem}>
                  <div className={styles.customerInfo}>
                    <div className={styles.customerAvatar}></div>
                    <span>{inv.customer}</span>
                  </div>
                  <div className={styles.invoiceDetails}>
                    {inv.description && <span className={styles.description}>{inv.description}</span>}
                    {inv.dueDate && (
                      <span className={styles.dueDate}>
                        Due: {new Date(inv.dueDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className={styles.invoiceActions}>
                    <span className={styles.invoiceAmount}>${(inv.amount / 100).toFixed(2)}</span>
                    <span className={`${styles.statusIndicator} ${styles[inv.status.toLowerCase()]}`}>
                      {inv.status}
                    </span>
                    <button
                      className={`${styles.invoiceButton} ${
                        inv.status === 'Pending' ? styles.generateInvoice : styles.resendInvoice
                      }`}
                      onClick={() =>
                        inv.status === 'Pending' ? resendInvoice({ ...inv, status: 'Overdue' }) : resendInvoice(inv)
                      }
                    >
                      {inv.status === 'Pending' ? 'Generate invoice' : 'Resend invoice'}
                    </button>
                    <button
                      className={styles.deleteInvoice}
                      onClick={() => deleteInvoice(inv.id)}
                    >
                      ❌
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicingPage;